import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plug, CheckCircle2, RefreshCw, Loader2, ExternalLink, Trash2, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Integrations() {
  const [providers, setProviders] = useState(null);
  const [dialog, setDialog] = useState(null); // provider object being connected
  const [creds, setCreds] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/integrations").then((r) => setProviders(r.data)).catch(() => setProviders([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("google_ads");
    if (g === "connected") toast.success("Google Ads connected. Run a sync to import ad spend.");
    else if (g === "noref") toast.error("Google didn't return authorization. Try again and approve access.");
    else if (g === "error") toast.error("Google Ads connection failed. Please try again.");
    if (g) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const openConnect = (p) => { setCreds({}); setDialog(p); };

  const startOAuth = () => {
    const cid = encodeURIComponent(creds.customer_id || "");
    window.location.href = `${BACKEND_URL}/api/integrations/google-ads/start?customer_id=${cid}`;
  };

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post(`/integrations/${dialog.provider}/connect`, { credentials: creds });
      toast.success(data.note || `${dialog.name} connected`);
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const sync = async (p) => {
    setBusy(`sync-${p.provider}`);
    try {
      const { data } = await api.post(`/integrations/${p.provider}/sync`);
      toast.success(`Synced — ${data.imported} new, ${data.updated} updated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const disconnect = async (p) => {
    setBusy(`dc-${p.provider}`);
    try {
      await api.delete(`/integrations/${p.provider}`);
      toast.success(`${p.name} disconnected`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!providers)
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <div className="grid md:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-56" />)}</div>
      </div>
    );

  return (
    <div>
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Automated data</p>
        <h1 className="font-display text-4xl font-light mt-1">Integrations</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Connect your ad platforms, call tracking and case-management tools to pull spend and lead data
          automatically — no more manual CSVs. CallRail, Meta Ads, Clio and Google Ads are live — connect them with your
          firm's own account.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {providers.map((p) => (
          <div key={p.provider} className="border border-zinc-200 bg-white p-6 flex flex-col" data-testid={`integration-${p.provider}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">{p.category}</p>
                <h3 className="font-display text-2xl mt-1 flex items-center gap-2">
                  {p.name}
                  {p.status === "coming_soon" && (
                    <span className="font-mono-data text-[10px] uppercase tracking-widest bg-[#D5A44A]/15 text-[#8A6A1F] px-2 py-0.5">Soon</span>
                  )}
                </h3>
              </div>
              {p.connected ? (
                <span className="flex items-center gap-1 text-[#156F52] text-sm font-medium" data-testid={`status-${p.provider}`}>
                  <CheckCircle2 className="h-4 w-4" /> Connected
                </span>
              ) : (
                <span className="flex items-center gap-1 text-zinc-400 text-sm" data-testid={`status-${p.provider}`}>
                  <Plug className="h-4 w-4" /> Not connected
                </span>
              )}
            </div>

            <p className="text-sm text-zinc-600 mt-3 flex-1">{p.description}</p>

            {p.connected && p.last_synced_at && (
              <p className="mt-3 text-xs text-zinc-500 flex items-center gap-1 font-mono-data">
                <Clock className="h-3 w-3" /> Last synced {new Date(p.last_synced_at).toLocaleString()}
                {p.last_sync_result && ` · ${p.last_sync_result.imported} new`}
              </p>
            )}

            <div className="mt-5 flex flex-wrap gap-2 items-center">
              {!p.connected && (
                <Button onClick={() => openConnect(p)} className="bg-[#0B1F33] text-white rounded-none h-9" data-testid={`connect-${p.provider}`}>
                  <Plug className="h-4 w-4 mr-1" /> Connect
                </Button>
              )}
              {p.connected && p.status === "live" && (
                <Button onClick={() => sync(p)} disabled={busy === `sync-${p.provider}`} className="bg-[#174A7E] text-white rounded-none h-9" data-testid={`sync-${p.provider}`}>
                  {busy === `sync-${p.provider}` ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Sync now
                </Button>
              )}
              {p.connected && (
                <Button onClick={() => openConnect(p)} variant="outline" className="rounded-none h-9 border-zinc-300 text-zinc-700" data-testid={`reconnect-${p.provider}`}>
                  <Plug className="h-4 w-4 mr-1" /> Reconnect
                </Button>
              )}
              {p.connected && (
                <Button onClick={() => disconnect(p)} disabled={busy === `dc-${p.provider}`} variant="outline" className="rounded-none h-9 border-zinc-300 text-zinc-600" data-testid={`disconnect-${p.provider}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> Disconnect
                </Button>
              )}
              <a href={p.docs} target="_blank" rel="noreferrer" className="text-xs text-[#174A7E] hover:underline flex items-center gap-1 ml-auto">
                API docs <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Connect {dialog?.name}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4 py-1">
              <div className="border border-zinc-200 bg-zinc-50 p-3">
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-2">How to get your credentials</p>
                <ol className="list-decimal list-inside space-y-1 text-xs text-zinc-600">
                  {dialog.howto.map((h, i) => <li key={i}>{h}</li>)}
                </ol>
              </div>
              {dialog.fields.map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input
                    type={f.type}
                    value={creds[f.key] || ""}
                    onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                    data-testid={`field-${dialog.provider}-${f.key}`}
                    className="mt-1 rounded-none"
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            {dialog?.oauth ? (
              <Button onClick={startOAuth} className="bg-[#0B1F33] text-white rounded-none" data-testid="connect-with-google-button">
                <Plug className="h-4 w-4 mr-2" /> Connect with Google
              </Button>
            ) : (
              <Button onClick={connect} disabled={busy === "connect"} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-connection-button">
                {busy === "connect" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {dialog?.status === "live" ? "Validate & connect" : "Save credentials"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
